/* eslint-disable no-param-reassign  */
import { createSlice } from '@reduxjs/toolkit';
import { concat, filter, assocPath, pathOr, includes } from 'ramda';
import { useDispatch } from 'react-redux';
import { FETCH_STATUSES } from 'utils/fetchStatusUtils';
import WaitlistLoanOfficersRepository from 'repositories/realEstateAgent/WaitlistLoanOfficersRepository';

const initialState = {
  loanOfficers: [],
  loanOfficersLoadingStatus: FETCH_STATUSES.idle,
  loanOfficersLoadingMoreStatus: FETCH_STATUSES.idle,
  meta: {},

  updatingStatus: FETCH_STATUSES.idle,
  batchUpdatingStatus: FETCH_STATUSES.idle,
};

const waitlistLoanOfficersSlice = createSlice({
  name: 'waitlistLoanOfficersSlice',
  initialState,
  reducers: {
    loadLoanOfficersStart(state) {
      state.loanOfficersLoadingStatus = FETCH_STATUSES.pending;
    },
    loadLoanOfficersSuccess(state, { payload }) {
      const { loanOfficers, meta } = payload;
      state.loanOfficers = loanOfficers;
      state.meta = meta;
      state.loanOfficersLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    loadLoanOfficersFail(state) {
      state.loanOfficersLoadingStatus = FETCH_STATUSES.failed;
    },
    loadMoreLoanOfficersStart(state) {
      state.loanOfficersLoadingMoreStatus = FETCH_STATUSES.pending;
    },
    loadMoreLoanOfficersSuccess(state, { payload }) {
      const { loanOfficers, meta } = payload;
      state.loanOfficers = concat(state.loanOfficers, loanOfficers);
      state.meta = meta;
      state.loanOfficersLoadingMoreStatus = FETCH_STATUSES.fulfilled;
    },
    loadMoreLoanOfficersFail(state) {
      state.loanOfficersLoadingMoreStatus = FETCH_STATUSES.failed;
    },
    approveLoanOfficerStart(state) {
      state.updatingStatus = FETCH_STATUSES.pending;
    },
    approveLoanOfficerSuccess(state, { payload }) {
      const { id } = payload;
      state.loanOfficers = filter(loanOfficer => loanOfficer.id !== id, state.loanOfficers);
      const totalCount = pathOr(0, ['totalCount'], state.meta);
      state.meta = assocPath(['totalCount'], totalCount - 1, state.meta);
      state.updatingStatus = FETCH_STATUSES.fulfilled;
    },
    approveLoanOfficerFail(state) {
      state.updatingStatus = FETCH_STATUSES.failed;
    },
    batchApproveLoanOfficersStart(state) {
      state.batchUpdatingStatus = FETCH_STATUSES.pending;
    },
    batchApproveLoanOfficersSuccess(state, { payload }) {
      const { ids } = payload;
      state.loanOfficers = filter(loanOfficer => !includes(loanOfficer.id, ids), state.loanOfficers);
      const totalCount = pathOr(0, ['totalCount'], state.meta);
      state.meta = assocPath(['totalCount'], totalCount - ids.length, state.meta);
      state.batchUpdatingStatus = FETCH_STATUSES.fulfilled;
    },
    batchApproveLoanOfficersFail(state) {
      state.batchUpdatingStatus = FETCH_STATUSES.failed;
    },
  },
});

export const { actions } = waitlistLoanOfficersSlice;

export default waitlistLoanOfficersSlice.reducer;

export const useAppActions = () => {
  const dispatch = useDispatch();

  const loadLoanOfficers = async params => {
    dispatch(actions.loadLoanOfficersStart());
    try {
      const { loanOfficers, meta } = await WaitlistLoanOfficersRepository.index(params);
      dispatch(actions.loadLoanOfficersSuccess({ loanOfficers, meta }));
    } finally {
      dispatch(actions.loadLoanOfficersFail());
    }
  };

  const loadMoreLoanOfficers = async params => {
    dispatch(actions.loadMoreLoanOfficersStart());
    try {
      const { loanOfficers, meta } = await WaitlistLoanOfficersRepository.index(params);
      dispatch(actions.loadMoreLoanOfficersSuccess({ loanOfficers, meta }));
    } finally {
      dispatch(actions.loadMoreLoanOfficersFail());
    }
  };

  const approveLoanOfficer = async (id, params) => {
    dispatch(actions.approveLoanOfficerStart());
    try {
      await WaitlistLoanOfficersRepository.update(id, params);
      dispatch(actions.approveLoanOfficerSuccess({ id }));
    } catch (error) {
      dispatch(actions.approveLoanOfficerFail());
      throw error;
    }
  };
  const batchApproveLoanOfficers = async params => {
    dispatch(actions.batchApproveLoanOfficersStart());
    try {
      await WaitlistLoanOfficersRepository.batchUpdate(params);
      dispatch(actions.batchApproveLoanOfficersSuccess({ ids: params.loanOfficersIds }));
    } catch (error) {
      dispatch(actions.batchApproveLoanOfficersFail());
      throw error;
    }
  };

  return {
    loadLoanOfficers,
    loadMoreLoanOfficers,
    approveLoanOfficer,
    batchApproveLoanOfficers,
  };
};
/* eslint-enable no-param-reassign */



// WEBPACK FOOTER //
// ./src/reducers/slices/realEstateAgent/waitlistLoanOfficersSlice.js